/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import BugIcon from '@atlaskit/icon/core/bug';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import twgLogo from '../../../assets/twg-logo.svg';
import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { usePanelContext } from '../../../hooks/use-panel-context';
import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { SiteSwitcher } from '../SiteSwitcher';

import { messages } from './messages';
import { NavButtons } from './NavButtons';
import { UserAvatarDropdown } from './UserAvatarDropdown';

/**
 * The fixed pixel height of the navigation bar. Exported so consumers (e.g.
 * the hero stage) can size themselves to fill the remaining viewport without
 * duplicating this magic number.
 */
export const NAV_BAR_HEIGHT_PX = 88;

const styles = cssMap({
	navBar: {
		width: '100%',
		backgroundColor: '#111213',
	},
	navBarContent: {
		width: '100%',
		maxWidth: '1416px',
		height: `${NAV_BAR_HEIGHT_PX}px`,
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
		paddingInline: token('space.400'),
		marginInline: 'auto',
	},
	logoContainer: {
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
		font: token('font.heading.medium'),
		fontFamily: token('font.family.brand.heading'),
		justifySelf: 'start',
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
	},
	logo: {
		width: '16px',
		height: '16px',
		'@media (max-width: 30rem)': {
			width: '32px',
			height: '32px',
		},
	},
	logoLabel: {
		'@media (max-width: 30rem)': {
			display: 'none',
		},
	},
	rightControls: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.200'),
		justifySelf: 'end',
	},
	loginButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.075'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		backgroundColor: '#111213',
		borderColor: token('color.icon.subtle'),
		color: '#B7B9BE',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		whiteSpace: 'nowrap',
		'&:hover': {
			cursor: 'pointer',
			borderColor: '#B7B9BE',
		},
	},
});

const navBarContentStyles = css({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
});

const leftNavStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.400'),
});

export const NavigationBar = (): JSX.Element => {
	const { identity: user, loading, error, attemptUserLogin } = useAtlassianUserContext();
	const { formatMessage } = useIntl();
	const { togglePanel, isOpen } = usePanelContext();
	const { fireEvent } = useAnalyticsEvents();

	const handleLoginClick = useCallback((): void => {
		fireEvent('ui.button.clicked.twgMicrositeLogin', { location: 'navBar' });
		attemptUserLogin();
	}, [fireEvent, attemptUserLogin]);

	return (
		<UFOSegment name="twg-microsite.nav-bar">
			<div css={styles.navBar}>
				<Box xcss={styles.navBarContent}>
					<div css={navBarContentStyles}>
						<div css={leftNavStyles}>
							<div css={styles.logoContainer}>
								<img src={twgLogo} alt={formatMessage(messages.logoAlt)} css={styles.logo} />
								{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
								<span css={styles.logoLabel}>
									<FormattedMessage {...messages.teamworkGraphBrand} />
								</span>
							</div>
							<NavButtons ariaLabel={formatMessage(messages.teamworkGraphBrand)} />
						</div>

						<div css={styles.rightControls}>
							{/* TODO: GRAPHYTE-2509 Error fallback / Suspense Fallback */}
							{loading && (
								<Spinner size="small" interactionName="twg-microsite.nav-bar.user-loading" />
							)}
							{error && (
								<button
									aria-label={formatMessage(messages.loginAriaLabel)}
									css={styles.loginButton}
									type="button"
									onClick={handleLoginClick}
								>
									<FormattedMessage {...messages.loginButton} />
								</button>
							)}

							{!loading && !error && user && <SiteSwitcher />}
							{!loading && !error && user && <UserAvatarDropdown user={user} />}

							{fg('twg_microsite_debug_panel') && (
								<IconButton
									icon={BugIcon}
									label="Debug Menu Toggle"
									appearance={isOpen ? 'discovery' : 'default'}
									onClick={togglePanel}
								/>
							)}
						</div>
					</div>
				</Box>
			</div>
		</UFOSegment>
	);
};
